/**
 * HR · Saudi labour rules.
 *
 * Every rate the HR suite computes with, where it comes from, and the
 * airport's chance to restate it. The defaults were written from the
 * published law and schemes and are proposed until HR or legal confirms them;
 * a restated rate re-prices every screen at once, because rates are computed
 * from the rules on every read and never stored.
 *
 * Also the end-of-service calculator: art. 84's award, cut by art. 85 for a
 * resignation, for any wage and length of service or for one employee.
 */
import { useMemo, useState } from "react";
import { useHrPeople, useHrRules, useHrSetRules } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, Field, Input, Select, Skeleton, Table } from "../../design/components";
import { eosb, eosbWage, RULE_SOURCES, type HrRules, type HrRulesPatch, type LeaveReason } from "../../domain/hr";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { useHrAccess } from "./access";
import { HrError, s, SampleBanner, sar, Section, SUITE } from "./shared";

interface RuleField {
  path: [keyof HrRules, string];
  label: string;
  unit: string;
}

const FIELDS: { title: string; fields: RuleField[] }[] = [
  {
    title: "GOSI",
    fields: [
      {
        path: ["gosi", "legacyAnnuityEmployer"],
        label: "Annuities, employer (registered before July 2024)",
        unit: "%",
      },
      {
        path: ["gosi", "legacyAnnuityEmployee"],
        label: "Annuities, employee (registered before July 2024)",
        unit: "%",
      },
      { path: ["gosi", "sanedEmployer"], label: "SANED, employer", unit: "%" },
      { path: ["gosi", "sanedEmployee"], label: "SANED, employee", unit: "%" },
      { path: ["gosi", "hazardsEmployer"], label: "Occupational hazards, employer", unit: "%" },
      { path: ["gosi", "baseMin"], label: "Contribution wage, floor", unit: "SAR" },
      { path: ["gosi", "baseMax"], label: "Contribution wage, cap", unit: "SAR" },
    ],
  },
  {
    title: "End of service",
    fields: [
      { path: ["eosb", "firstYears"], label: "Years at the lower rate", unit: "years" },
      { path: ["eosb", "firstRateMonths"], label: "Lower rate, months of wage a year", unit: "months" },
      { path: ["eosb", "laterRateMonths"], label: "Later rate, months of wage a year", unit: "months" },
    ],
  },
  {
    title: "Leave and hours",
    fields: [
      { path: ["leave", "days"], label: "Annual leave", unit: "days" },
      { path: ["leave", "daysAfter"], label: "Annual leave after the qualifying years", unit: "days" },
      { path: ["leave", "afterYears"], label: "Qualifying years", unit: "years" },
      { path: ["hours", "weeklyMax"], label: "Hours a week, most", unit: "hours" },
      { path: ["hours", "workdaysPerWeek"], label: "Working days a week", unit: "days" },
      { path: ["hours", "publicHolidayDays"], label: "Public holidays a year", unit: "days" },
      { path: ["hours", "sickAllowanceDays"], label: "Sick days allowed for", unit: "days" },
      { path: ["overtime", "premiumOfBasic"], label: "Overtime premium, share of the hourly basic", unit: "×" },
    ],
  },
  {
    title: "Non-Saudi staff",
    fields: [
      { path: ["expat", "levyMonthly"], label: "Work-permit levy, a month", unit: "SAR" },
      { path: ["expat", "iqamaAnnual"], label: "Iqama, a year", unit: "SAR" },
      { path: ["expat", "workPermitAnnual"], label: "Work permit, a year", unit: "SAR" },
      { path: ["expat", "exitReentryAnnual"], label: "Exit/re-entry, a year", unit: "SAR" },
      { path: ["expat", "ticketAnnual"], label: "Annual ticket (contract)", unit: "SAR" },
    ],
  },
  {
    title: "Medical and Nitaqat",
    fields: [
      { path: ["medicalAnnualDefault", ""], label: "Medical premium a year, when not on the person", unit: "SAR" },
      { path: ["nitaqat", "fullAtWage"], label: "A Saudi counts in full from", unit: "SAR" },
      { path: ["nitaqat", "halfAtWage"], label: "A Saudi counts as half from", unit: "SAR" },
      { path: ["nitaqat", "targetPct"], label: "The airport's Nitaqat target (from Qiwa)", unit: "%" },
    ],
  },
];

const get = (r: HrRules, [a, b]: RuleField["path"]): number | null => {
  const top = r[a] as unknown;
  if (!b) return top as number;
  return ((top as Record<string, unknown>)[b] as number | null) ?? null;
};

export function HrRulesPage() {
  const session = useSession();
  const q = useHrRules();
  const canEdit = useHrAccess().can.admin;
  const v = q.data;

  useScreenContext(
    v
      ? {
          route: "/hr-rules",
          title: "Saudi labour rules",
          summary: [
            v.rules.basis === "default"
              ? t("The published defaults are in force, proposed until HR confirms them.")
              : t("The airport has restated some rules; the rest are the published defaults."),
            t("GOSI for a Saudi registered before July 2024: employer {er}%, employee {ee}%.", {
              er: v.rules.gosi.legacyAnnuityEmployer + v.rules.gosi.sanedEmployer + v.rules.gosi.hazardsEmployer,
              ee: v.rules.gosi.legacyAnnuityEmployee + v.rules.gosi.sanedEmployee,
            }),
            t("End of service: {a} of a month's wage a year for {y} years, then {b}.", {
              a: v.rules.eosb.firstRateMonths,
              y: v.rules.eosb.firstYears,
              b: v.rules.eosb.laterRateMonths,
            }),
          ],
          offers: [
            {
              label: t("What is an employee's end of service?"),
              ask: "how is end of service calculated in Saudi Arabia?",
            },
            { label: t("What does GOSI cost us?"), ask: "what are the GOSI contribution rates?" },
          ],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })} title="Saudi labour rules">
      <SampleBanner />
      {q.isLoading && <Skeleton rows={8} />}
      {q.error && <HrError error={q.error} retry={() => void q.refetch()} />}
      {v && (
        <>
          <div className={s.coverage} data-testid="hr-rules-basis">
            <div>
              <b>{t("Proposed.")}</b>{" "}
              {t(
                "Based on published law as of {date}. HR or legal should confirm each rate with GOSI, Qiwa, and the Ministry before relying on costs.",
                { date: v.rules.asOf },
              )}
            </div>
            {v.updatedAt && (
              <div>
                {t("Restated by {by} on {at}.", { by: v.updatedBy ?? t("someone"), at: v.updatedAt.slice(0, 10) })}
              </div>
            )}
          </div>
          <Sources />
          <RulesForm rules={v.rules} defaults={v.defaults} canEdit={canEdit} />
          <Schedule rules={v.rules} />
          <Calculator rules={v.rules} />
        </>
      )}
    </Page>
  );
}

function Sources() {
  return (
    <Card title={t("Where each rule comes from")} collapsible>
      <Table>
        <thead>
          <tr>
            <th>{t("Rule")}</th>
            <th>{t("Source")}</th>
            <th>{t("Kind")}</th>
          </tr>
        </thead>
        <tbody>
          {RULE_SOURCES.map((r) => (
            <tr key={r.key}>
              <td>{t(r.rule)}</td>
              <td>{t(r.source)}</td>
              <td>{r.kind === "law" ? t("law") : <span className={s.tag}>{t("practice")}</span>}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function RulesForm({ rules, defaults, canEdit }: { rules: HrRules; defaults: HrRules; canEdit: boolean }) {
  const save = useHrSetRules();
  const initial = useMemo(() => {
    const out: Record<string, string> = {};
    for (const g of FIELDS) for (const f of g.fields) out[f.path.join(".")] = String(get(rules, f.path) ?? "");
    out["eosb.wage"] = rules.eosb.wage;
    return out;
  }, [rules]);
  const [form, setForm] = useState(initial);
  const [done, setDone] = useState(false);

  const bad = Object.entries(form).filter(([k, v]) => k !== "eosb.wage" && v.trim() !== "" && !(Number(v) >= 0));

  /* the restated set: only what differs from the published default */
  const restated = (): HrRulesPatch => {
    const out: Record<string, Record<string, unknown> | number> = {};
    for (const g of FIELDS)
      for (const f of g.fields) {
        const raw = form[f.path.join(".")].trim();
        const value = raw === "" ? null : Number(raw);
        const def = get(defaults, f.path);
        if (value === def || (value == null && f.path[1] !== "targetPct")) continue;
        const [a, b] = f.path;
        if (!b) out[a] = value as number;
        else out[a] = { ...(out[a] as Record<string, unknown> | undefined), [b]: value };
      }
    if (form["eosb.wage"] !== defaults.eosb.wage)
      out.eosb = { ...(out.eosb as Record<string, unknown> | undefined), wage: form["eosb.wage"] };
    return out as HrRulesPatch;
  };

  return (
    <Card
      title={t("The rates in force")}
      sub={
        canEdit
          ? t("Change a figure and save. Untouched figures keep their defaults.")
          : t("Only the HR super admin changes the rules.")
      }
    >
      <form
        className={s.dialogForm}
        onSubmit={(e) => {
          e.preventDefault();
          if (bad.length) return;
          setDone(false);
          save.mutate(restated(), { onSuccess: () => setDone(true) });
        }}
      >
        {FIELDS.map((g) => (
          <Section key={g.title} title={t(g.title)}>
            {g.fields.map((f) => {
              const k = f.path.join(".");
              const def = get(defaults, f.path);
              const changed = form[k].trim() !== String(def ?? "");
              return (
                <Field key={k} label={`${t(f.label)} (${t(f.unit)})`}>
                  <Input
                    value={form[k]}
                    disabled={!canEdit}
                    inputMode="decimal"
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`hr-rule-${k}`}
                    style={changed ? { borderColor: "var(--amber)" } : undefined}
                  />
                  {changed && (
                    <span className={s.dim}>{t("default {v}", { v: def == null ? t("none") : String(def) })}</span>
                  )}
                </Field>
              );
            })}
            {g.title === "End of service" && (
              <Field label="Wage the award is paid on">
                <Select
                  value={form["eosb.wage"]}
                  disabled={!canEdit}
                  onChange={(e) => setForm({ ...form, "eosb.wage": e.target.value })}
                >
                  <option value="actual">{t("The actual wage: basic and every fixed allowance (art. 2)")}</option>
                  <option value="basic_housing">{t("Basic and housing only (where the contract says so)")}</option>
                </Select>
              </Field>
            )}
          </Section>
        ))}
        {bad.length > 0 && <p className={s.err}>{t("Every rate is a number, not negative.")}</p>}
        {save.error && <p className={s.err}>{save.error instanceof Error ? save.error.message : String(save.error)}</p>}
        {done && <p className={s.ok}>{t("Saved. All HR figures now use these rules.")}</p>}
        {canEdit && (
          <div className={s.formFoot}>
            <Button type="submit" variant="primary" disabled={save.isPending} data-testid="hr-rules-save">
              {save.isPending ? t("Saving…") : t("Save the rules")}
            </Button>
            <Button
              onClick={() => {
                setDone(false);
                save.mutate({}, { onSuccess: () => setDone(true) });
              }}
              disabled={save.isPending}
            >
              {t("Return to the published defaults")}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}

function Schedule({ rules }: { rules: HrRules }) {
  const today = new Date().toISOString().slice(0, 10);
  const inForce = [...rules.gosi.newEntrantSchedule].filter((st) => st.from <= today).pop();
  return (
    <Card
      title={t("GOSI annuities for new entrants")}
      sub={t("Rising rate for Saudis registered from {d}, plus SANED and hazards.", {
        d: rules.gosi.newEntrantSince,
      })}
    >
      <Table>
        <thead>
          <tr>
            <th>{t("From")}</th>
            <th className={s.num}>{t("Employer")}</th>
            <th className={s.num}>{t("Employee")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rules.gosi.newEntrantSchedule.map((st) => (
            <tr key={st.from}>
              <td className={s.mono}>{st.from}</td>
              <td className={s.num}>{st.employer}%</td>
              <td className={s.num}>{st.employee}%</td>
              <td>{st === inForce && <span className={s.tag}>{t("in force")}</span>}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function Calculator({ rules }: { rules: HrRules }) {
  const people = useHrPeople();
  const [who, setWho] = useState("");
  const [wage, setWage] = useState("10000");
  const [years, setYears] = useState("7");
  const [reason, setReason] = useState<LeaveReason>("termination");
  const pick = (id: string) => {
    setWho(id);
    const e = people.data?.employees.find((x) => x.id === id);
    if (e && e.employment === "direct") {
      setWage(String(eosbWage(e, rules)));
      setYears(e.serviceYears.toFixed(2));
    }
  };
  const w = Number(wage);
  const y = Number(years);
  const r = w >= 0 && y >= 0 ? eosb(w, y, reason, rules) : null;
  return (
    <Card
      title={t("End-of-service calculator")}
      sub={t("Labour Law arts. 84 and 85, with the rules above.")}
      testId="hr-eosb"
    >
      <div className={s.form}>
        <Field label="Employee (optional)">
          <Select value={who} onChange={(e) => pick(e.target.value)}>
            <option value="">{t("Any wage and service")}</option>
            {(people.data?.employees ?? [])
              .filter((e) => e.employment === "direct")
              .map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name} · {e.employeeNo}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Monthly wage the award is paid on (SAR)">
          <Input
            value={wage}
            onChange={(e) => setWage(e.target.value)}
            inputMode="decimal"
            data-testid="hr-eosb-wage"
          />
        </Field>
        <Field label="Years of service">
          <Input
            value={years}
            onChange={(e) => setYears(e.target.value)}
            inputMode="decimal"
            data-testid="hr-eosb-years"
          />
        </Field>
        <Field label="How the service ends">
          <Select
            value={reason}
            onChange={(e) => setReason(e.target.value as LeaveReason)}
            data-testid="hr-eosb-reason"
          >
            <option value="termination">{t("Ended by the employer")}</option>
            <option value="contract_end">{t("Fixed-term contract ended")}</option>
            <option value="resignation">{t("Resignation")}</option>
          </Select>
        </Field>
      </div>
      {r && (
        <dl className={s.kv} style={{ marginTop: 12 }}>
          <dt>{t("First {y} years × {m} month's wage", { y: r.firstYears, m: rules.eosb.firstRateMonths })}</dt>
          <dd>{sar(r.firstAmount)}</dd>
          <dt>{t("Then {y} years × {m} month's wage", { y: r.laterYears, m: rules.eosb.laterRateMonths })}</dt>
          <dd>{sar(r.laterAmount)}</dd>
          <dt>{t("The full award (art. 84)")}</dt>
          <dd>{sar(r.full)}</dd>
          {reason === "resignation" && (
            <>
              <dt>{t("Kept on resignation (art. 85)")}</dt>
              <dd>{t("{f} of it", { f: r.factor === 0 ? "0" : r.factor === 1 ? "1" : r.factor < 0.5 ? "⅓" : "⅔" })}</dd>
            </>
          )}
          <dt>
            <b>{t("Payable")}</b>
          </dt>
          <dd>
            <b data-testid="hr-eosb-amount">{sar(r.amount)}</b>
          </dd>
        </dl>
      )}
    </Card>
  );
}
